import React, { createContext, useContext, useState, useEffect } from "react";

/**
 * Woon Verder – i18n dictionary
 * Supported languages: NL (default), EN, DE, ES
 *
 * Key naming: <area>.<key>  (dot notation)
 * Only add strings that are shown to end-users. Admin/back-office UI stays NL by design.
 */

const dict = {
  nl: {
    // --- Global ---
    "back_home": "Terug naar homepage",
    "read_more": "Meer informatie",
    "loading": "Laden…",
    "yes": "Ja",
    "no": "Nee",
    "cancel": "Annuleren",
    "save": "Opslaan",
    "delete": "Verwijderen",
    "confirm": "Bevestigen",
    "close": "Sluiten",

    // --- Nav / CTA ---
    "nav.properties": "Woningen",
    "nav.how": "Hoe het werkt",
    "nav.sellers": "Voor Verkopers",
    "nav.careers": "Werk bij ons",
    "nav.about": "Over Ons",
    "nav.contact": "Contact",
    "nav.login": "Inloggen",
    "nav.logout": "Uitloggen",
    "nav.my_account": "Mijn account",
    "nav.admin_dashboard": "Admin Dashboard",
    "cta.submit_property": "Mijn woning aanmelden",
    "cta.search_property": "Ik zoek een woning",
    "cta.free_profile": "Maak gratis een zoekprofiel",
    "cta.view_property": "Bekijk woning",
    "cta.interest": "Ik heb interesse",
    "cta.register_free": "Gratis account aanmaken",
    "cta.have_account": "Ik heb al een account",

    // --- Hero / Home ---
    "hero.badge": "Nederlandse launch · Internationale ambitie",
    "hero.title": "Zoek niet zomaar een huis. Vind waar je écht verder kunt wonen.",
    "hero.subtitle": "Woon Verder verbindt geverifieerde verkopers direct met woningzoekers die daadwerkelijk zoeken. Nederlandse launch, internationale ambitie.",
    "hero.tag_seller": "Misschien heeft uw woning geen nieuwe prijs nodig, maar een nieuwe koper.",
    "hero.tag_buyer": "Vertel ons wat u zoekt. Wij zoeken verder.",
    "hero.verified": "Elke woning admin-geverifieerd",
    "hero.never_auto": "Nooit automatisch gepubliceerd",
    "hero.match_label": "Match score",
    "hero.match_example": "Vrijstaand · 4 slk · Flevoland · <€ 550k",
    "hero.for_sellers": "Voor verkopers",
    "hero.for_buyers": "Voor kopers",

    "section.featured": "Uitgelichte woningen",
    "section.how_it_works": "Hoe Woon Verder werkt",
    "section.offer": "Aanbod",
    "section.process": "Proces",
    "section.all_properties": "Alle woningen bekijken",
    "home.step_label": "Stap",
    "home.step1_title": "Verkoper meldt aan",
    "home.step1_desc": "U vult woninggegevens in en upload zelf foto's — óf u vraagt professionele Woon Verder-fotografie aan.",
    "home.step2_title": "Woon Verder controleert",
    "home.step2_desc": "Ons team beoordeelt de aanvraag, plant zo nodig een afspraak en controleert foto's & gegevens.",
    "home.step3_title": "Matching & publicatie",
    "home.step3_desc": "Pas na goedkeuring gaat de woning live én wordt automatisch gematcht met passende zoekprofielen.",
    "home.full_explanation": "Volledige uitleg",
    "home.faq_kicker": "Veelgestelde vragen",
    "home.faq_title": "Antwoorden voor kopers & verkopers",
    "home.seller_kicker": "Verkopers",
    "home.seller_band_title": "Professionele fotografie via Woon Verder",
    "home.seller_band_desc": "Foto's die verkopen. Onze fotografen leggen uw woning vast in licht en compositie waarmee kopers écht op afkomen. Fotografie is een aparte, betaalde service met prijzen op aanvraag — u kunt ook uw eigen foto's aanleveren.",
    "home.seller_band_cta": "Aanmelden inclusief fotografie",
    "home.seller_band_photo_cta": "Fotografie op aanvraag",

    // --- Auth Gate ---
    "authgate.badge": "Login vereist",
    "authgate.title": "Log in om het volledige aanbod te zien",
    "authgate.subtitle": "Woon Verder is een gesloten matching-platform. Maak in 30 seconden een gratis account aan en ontdek woningen die écht bij u passen.",
    "authgate.bullet1": "Persoonlijke woningmatch op basis van uw wensen",
    "authgate.bullet2": "Direct inzicht in match-score per woning",
    "authgate.bullet3": "Sla favorieten op en volg biedingen live",
    "authgate.bullet4": "Gratis — geen abonnement, geen creditcard",
    "authgate.terms_prefix": "Door een account aan te maken gaat u akkoord met onze",
    "authgate.terms": "algemene voorwaarden",
    "authgate.and": "en",
    "authgate.privacy": "privacyverklaring",
    "authgate.match_label": "Uw match",
    "authgate.match_desc": "Op basis van uw profiel",
    "authgate.default_properties_title": "Log in om het volledige woningaanbod te bekijken",
    "authgate.default_property_title": "Log in om deze woning te bekijken",
    "authgate.default_property_subtitle": "Woningdetails zijn zichtbaar voor geregistreerde gebruikers. Maak in 30 seconden een gratis account aan of log in om verder te gaan.",

    // --- Login ---
    "login.title": "Inloggen",
    "login.subtitle": "Log in op uw Woon Verder account.",
    "login.email": "Email",
    "login.password": "Wachtwoord",
    "login.submit": "Inloggen",
    "login.submitting": "Inloggen…",
    "login.no_account": "Nog geen account?",
    "login.register_link": "Registreren",
    "login.welcome_back": "Welkom terug",

    // --- Register ---
    "register.title": "Gratis account aanmaken",
    "register.subtitle": "30 seconden — geen creditcard nodig.",
    "register.subtitle_approval": "Nieuwe accounts worden handmatig goedgekeurd door Woon Verder (meestal binnen 1 werkdag). Uw gegevens landen direct in ons admin-dashboard — zodra goedgekeurd kunt u inloggen.",
    "register.name": "Naam",
    "register.email": "Email",
    "register.phone": "Telefoon",
    "register.phone_hint": "Optioneel. Internationaal formaat, bv. +31 6 12 34 56 78",
    "register.country": "Land",
    "register.password": "Wachtwoord (min 6 tekens)",
    "register.role_label": "Ik ben een",
    "register.role_buyer": "Koper / zoeker",
    "register.role_seller": "Verkoper",
    "register.submit": "Account aanmaken",
    "register.submitting": "Aanmaken…",
    "register.have_account": "Al een account?",
    "register.login_link": "Inloggen",
    "register.created": "Account aangemaakt",
    "register.err_name": "Vul uw volledige naam in (min. 2 tekens)",
    "register.err_email": "Vul een geldig e-mailadres in",
    "register.err_phone": "Ongeldig telefoonnummer — gebruik 6-20 cijfers (spaties, +, -, (), / toegestaan)",
    "register.err_country": "Vul uw land in",
    "register.err_password": "Wachtwoord moet minimaal 6 tekens zijn",
    "register.err_server_unexpected": "Er ging iets mis. Probeer het opnieuw of neem contact op via info@woonverder.eu.",
    "register.country_hint": "Bv. Nederland, België, Deutschland of ISO-code (NL, BE, DE…)",
    "register.pending_title": "Bedankt — uw aanvraag is verzonden",
    "register.pending_body_prefix": "Uw gegevens zijn opgeslagen onder",
    "register.pending_body_suffix": "Onze admin ziet uw aanvraag direct in het dashboard en keurt uw account normaal binnen 1 werkdag goed. Daarna kunt u inloggen.",
    "register.pending_step1": "Admin ziet uw aanvraag direct in het Woon Verder dashboard",
    "register.pending_step2": "Admin keurt uw account doorgaans binnen 1 werkdag goed",
    "register.pending_step3": "Zodra goedgekeurd kunt u inloggen en het platform gebruiken",

    // --- Property card / detail ---
    "prop.verified": "✓ Geverifieerd",
    "prop.match_suffix": "% Match",
    "prop.slk": "slk",
    "prop.rooms": "kamers",
    "prop.m2": "m²",
    "prop.energy_label": "Energielabel",

    // --- Properties list ---
    "properties.kicker": "Aanbod",
    "properties.title": "Woningen",
    "properties.filter_city_ph": "Plaats (bv. Amsterdam)",
    "properties.filter_all_types": "Alle woningtypes",
    "properties.filter_min_bedrooms_ph": "Min. slaapkamers",
    "properties.filter_max_price_ph": "Max. prijs €",
    "properties.search": "Zoeken",
    "properties.empty_title": "Geen passende woningen gevonden.",
    "properties.empty_cta": "Maak een zoekprofiel",

    // --- Footer ---
    "footer.tagline": "Zoek verder naar wat bij je past.",
    "footer.subtag": "Internationaal real-estate matching platform. Nederlandse launch, wereldwijde ambitie.",
    "footer.section_platform": "Platform",
    "footer.section_company": "Bedrijf",
    "footer.link_search_profile": "Gratis zoekprofiel",
    "footer.link_zoekservice": "Zoekservice",
    "footer.link_mine": "Mijn Woon Verder",
    "footer.link_privacy": "Privacy",
    "footer.link_terms": "Voorwaarden",
    "footer.link_complaints": "Klachten",
    "footer.rights": "Alle rechten voorbehouden.",
    "footer.launch_line": "Nederlandse launch. Klaar voor internationale schaal.",

    // --- Contact ---
    "contact.kicker": "Contact",
    "contact.title": "Wij helpen u graag verder.",
    "contact.subtitle": "Vragen over uw woning, uw zoekprofiel of onze fotografie service? Neem gerust contact op — wij reageren binnen één werkdag.",
    "contact.email_label": "Email",
    "contact.region_label": "Regio",
    "contact.region_value": "Heel Nederland",
    "contact.languages_label": "Talen",
    "contact.form_title": "Stuur ons een bericht",
    "contact.name": "Naam",
    "contact.email_field": "Email",
    "contact.subject": "Onderwerp",
    "contact.subj_general": "Algemene vraag",
    "contact.subj_seller": "Vraag als verkoper",
    "contact.subj_buyer": "Vraag als koper / woningzoeker",
    "contact.subj_photo": "Fotografie service",
    "contact.subj_privacy": "Privacy / AVG",
    "contact.subj_complaint": "Klacht",
    "contact.message": "Uw bericht",
    "contact.submit": "Verstuur bericht",
    "contact.sending": "Verzenden…",
    "contact.privacy_note_prefix": "Door te verzenden gaat u akkoord met onze",
    "contact.sent_title": "Bericht verstuurd",
    "contact.sent_body": "Bedankt! Wij hebben uw bericht ontvangen en reageren binnen één werkdag. U ontvangt ook een bevestiging per e-mail.",
    "contact.error_fields": "Vul alstublieft alle velden in.",

    // --- Seller landing ---
    "seller.kicker": "Voor verkopers",
    "seller.title": "Staat uw woning al te lang te koop?",
    "seller.notice": "Uw woning wordt NIET automatisch gepubliceerd. Woon Verder controleert eerst uw aanvraag, foto's en gegevens. Pas na goedkeuring wordt de woning zichtbaar.",
    "seller.how_title": "Zo werkt het",
    "seller.step1": "Meld uw woning aan (gratis).",
    "seller.step2": "Upload zelf foto's — of vraag Woon Verder-fotografie aan.",
    "seller.step3": "Wij controleren uw aanvraag en nemen contact op.",
    "seller.step4": "Indien nodig plannen we een verificatie-afspraak.",
    "seller.step5": "Na goedkeuring publiceren wij uw woning.",
    "seller.step6": "Wij matchen uw woning aan actieve zoekprofielen.",
    "seller.step7": "Wij begeleiden bezichtigingen en biedingen.",
    "seller.step8": "Bij succesvolle verkoop geldt 2,5% (excl. btw) van de verkoopprijs.",
    "seller.photo_title": "Woon Verder fotografie",
    "seller.photo_desc": "Professionele fotograaf voor uw woning. Prijzen op aanvraag — afhankelijk van type woning, oppervlakte en gewenste dienst maken wij een maatwerk-offerte.",
    "seller.photo_cta": "Aanvraag doen",
    "seller.own_photo_title": "Zelf foto's aanleveren",
    "seller.own_photo_desc": "Gratis — u upload uw eigen foto's tijdens de aanmelding. Wij controleren de kwaliteit vóór publicatie.",

    // --- Toasts (buyer-facing) ---
    "toast.interest_sent": "Bedankt, uw interesse is doorgegeven.",
    "toast.viewing_sent": "Bezichtiging aangevraagd.",
    "toast.offer_sent": "Uw bod is doorgegeven.",
    "toast.generic_error": "Er ging iets mis. Probeer het opnieuw.",

    // --- Cookie banner ---
    "cookie.title": "Uw privacy staat voorop",
    "cookie.desc_prefix": "Woon Verder gebruikt cookies zodat de site werkt en om te leren wat beter kan. U bepaalt wat u toelaat. Lees onze",
    "cookie.privacy_link": "privacyverklaring",
    "cookie.cat_necessary": "Noodzakelijk",
    "cookie.cat_necessary_desc": "Voor het functioneren van de site: login, taal en sessie. Altijd actief.",
    "cookie.cat_preferences": "Voorkeuren",
    "cookie.cat_preferences_desc": "Onthouden van uw taal, filters en zoekopdrachten voor een betere ervaring.",
    "cookie.cat_analytics": "Analytics",
    "cookie.cat_analytics_desc": "Geanonimiseerd meten van paginabezoeken zodat we het platform kunnen verbeteren.",
    "cookie.cat_marketing": "Marketing",
    "cookie.cat_marketing_desc": "Momenteel niet actief. Zodra dit verandert vragen wij opnieuw uw toestemming.",
    "cookie.always": "Altijd",
    "cookie.customize": "Aanpassen",
    "cookie.save_choice": "Mijn keuze bewaren",
    "cookie.only_necessary": "Alleen noodzakelijk",
    "cookie.accept_all": "Alles accepteren",
  },

  en: {
    "back_home": "Back to homepage",
    "read_more": "Learn more",
    "loading": "Loading…",
    "yes": "Yes",
    "no": "No",
    "cancel": "Cancel",
    "save": "Save",
    "delete": "Delete",
    "confirm": "Confirm",
    "close": "Close",

    "nav.properties": "Properties",
    "nav.how": "How it works",
    "nav.sellers": "For Sellers",
    "nav.careers": "Careers",
    "nav.about": "About",
    "nav.contact": "Contact",
    "nav.login": "Sign in",
    "nav.logout": "Sign out",
    "nav.my_account": "My account",
    "nav.admin_dashboard": "Admin dashboard",
    "cta.submit_property": "List my property",
    "cta.search_property": "Find a property",
    "cta.free_profile": "Create free search profile",
    "cta.view_property": "View property",
    "cta.interest": "I'm interested",
    "cta.register_free": "Create free account",
    "cta.have_account": "I already have an account",

    "hero.badge": "Dutch launch · International ambition",
    "hero.title": "Don't just search for a house. Find where you can truly live further.",
    "hero.subtitle": "Woon Verder connects verified sellers directly with real, active buyers. Launching in the Netherlands. Built for international scale.",
    "hero.tag_seller": "Maybe your home doesn't need a new price, but a new buyer.",
    "hero.tag_buyer": "Tell us what you're looking for. We'll search further.",
    "hero.verified": "Every property admin-verified",
    "hero.never_auto": "Never auto-published",
    "hero.match_label": "Match score",
    "hero.match_example": "Detached · 4 bd · Flevoland · <€ 550k",
    "hero.for_sellers": "For sellers",
    "hero.for_buyers": "For buyers",

    "section.featured": "Featured properties",
    "section.how_it_works": "How Woon Verder works",
    "section.offer": "Listings",
    "section.process": "Process",
    "section.all_properties": "View all properties",
    "home.step_label": "Step",
    "home.step1_title": "Seller signs up",
    "home.step1_desc": "You fill in property details and upload photos yourself — or request professional Woon Verder photography.",
    "home.step2_title": "Woon Verder reviews",
    "home.step2_desc": "Our team reviews the application, schedules an appointment if needed and verifies photos & data.",
    "home.step3_title": "Matching & publication",
    "home.step3_desc": "Only after approval does the property go live and get automatically matched to relevant search profiles.",
    "home.full_explanation": "Full explanation",
    "home.faq_kicker": "Frequently asked questions",
    "home.faq_title": "Answers for buyers & sellers",
    "home.seller_kicker": "Sellers",
    "home.seller_band_title": "Professional photography via Woon Verder",
    "home.seller_band_desc": "Photos that sell. Our photographers capture your property with the light and composition that make buyers reach out. Photography is a separate paid service — prices on request. You can also provide your own photos.",
    "home.seller_band_cta": "List with photography included",
    "home.seller_band_photo_cta": "Photography on request",

    "authgate.badge": "Sign-in required",
    "authgate.title": "Sign in to view the full inventory",
    "authgate.subtitle": "Woon Verder is a closed matching platform. Create a free account in 30 seconds and discover homes that actually fit you.",
    "authgate.bullet1": "Personal home match based on your wishes",
    "authgate.bullet2": "Instant match-score per property",
    "authgate.bullet3": "Save favourites and track bids live",
    "authgate.bullet4": "Free — no subscription, no credit card",
    "authgate.terms_prefix": "By creating an account you agree to our",
    "authgate.terms": "terms of service",
    "authgate.and": "and",
    "authgate.privacy": "privacy statement",
    "authgate.match_label": "Your match",
    "authgate.match_desc": "Based on your profile",
    "authgate.default_properties_title": "Sign in to view the full property inventory",
    "authgate.default_property_title": "Sign in to view this property",
    "authgate.default_property_subtitle": "Property details are visible to registered users. Create a free account in 30 seconds or sign in to continue.",

    "login.title": "Sign in",
    "login.subtitle": "Sign in to your Woon Verder account.",
    "login.email": "Email",
    "login.password": "Password",
    "login.submit": "Sign in",
    "login.submitting": "Signing in…",
    "login.no_account": "No account yet?",
    "login.register_link": "Register",
    "login.welcome_back": "Welcome back",

    "register.title": "Create your free account",
    "register.subtitle": "30 seconds — no credit card required.",
    "register.subtitle_approval": "New accounts are manually approved by Woon Verder (usually within 1 business day). Your details land directly in our admin dashboard — as soon as approved you can sign in.",
    "register.name": "Name",
    "register.email": "Email",
    "register.phone": "Phone",
    "register.phone_hint": "Optional. International format, e.g. +31 6 12 34 56 78",
    "register.country": "Country",
    "register.password": "Password (min 6 characters)",
    "register.role_label": "I am a",
    "register.role_buyer": "Buyer / searcher",
    "register.role_seller": "Seller",
    "register.submit": "Create account",
    "register.submitting": "Creating…",
    "register.have_account": "Already have an account?",
    "register.login_link": "Sign in",
    "register.created": "Account created",
    "register.err_name": "Enter your full name (min 2 characters)",
    "register.err_email": "Enter a valid email address",
    "register.err_phone": "Invalid phone — use 6-20 digits (spaces, +, -, (), / allowed)",
    "register.err_country": "Enter your country",
    "register.err_password": "Password must be at least 6 characters",
    "register.err_server_unexpected": "Something went wrong. Please try again or contact info@woonverder.eu.",
    "register.country_hint": "E.g. Netherlands, Germany, Spain or ISO code (NL, DE, ES…)",
    "register.pending_title": "Thanks — your request has been sent",
    "register.pending_body_prefix": "Your details are stored under",
    "register.pending_body_suffix": "Our admin sees your request immediately in the dashboard and usually approves accounts within 1 business day. After that you can sign in.",
    "register.pending_step1": "Admin sees your request immediately in the Woon Verder dashboard",
    "register.pending_step2": "Admin usually approves your account within 1 business day",
    "register.pending_step3": "Once approved you can sign in and use the platform",

    "prop.verified": "✓ Verified",
    "prop.match_suffix": "% Match",
    "prop.slk": "bd",
    "prop.rooms": "rooms",
    "prop.m2": "m²",
    "prop.energy_label": "Energy label",

    "properties.kicker": "Listings",
    "properties.title": "Properties",
    "properties.filter_city_ph": "City (e.g. Amsterdam)",
    "properties.filter_all_types": "All property types",
    "properties.filter_min_bedrooms_ph": "Min. bedrooms",
    "properties.filter_max_price_ph": "Max. price €",
    "properties.search": "Search",
    "properties.empty_title": "No matching properties found.",
    "properties.empty_cta": "Create a search profile",

    "footer.tagline": "Search further for what fits you.",
    "footer.subtag": "International real-estate matching platform. Dutch launch, global ambition.",
    "footer.section_platform": "Platform",
    "footer.section_company": "Company",
    "footer.link_search_profile": "Free search profile",
    "footer.link_zoekservice": "Search service",
    "footer.link_mine": "My Woon Verder",
    "footer.link_privacy": "Privacy",
    "footer.link_terms": "Terms",
    "footer.link_complaints": "Complaints",
    "footer.rights": "All rights reserved.",
    "footer.launch_line": "Dutch launch. Ready for international scale.",

    "contact.kicker": "Contact",
    "contact.title": "We're happy to help.",
    "contact.subtitle": "Questions about your property, search profile or photography service? Reach out — we reply within one business day.",
    "contact.email_label": "Email",
    "contact.region_label": "Region",
    "contact.region_value": "All of the Netherlands",
    "contact.languages_label": "Languages",
    "contact.form_title": "Send us a message",
    "contact.name": "Name",
    "contact.email_field": "Email",
    "contact.subject": "Subject",
    "contact.subj_general": "General question",
    "contact.subj_seller": "As a seller",
    "contact.subj_buyer": "As a buyer / searcher",
    "contact.subj_photo": "Photography service",
    "contact.subj_privacy": "Privacy / GDPR",
    "contact.subj_complaint": "Complaint",
    "contact.message": "Your message",
    "contact.submit": "Send message",
    "contact.sending": "Sending…",
    "contact.privacy_note_prefix": "By submitting you agree to our",
    "contact.sent_title": "Message sent",
    "contact.sent_body": "Thanks! We've received your message and will reply within one business day. You'll also receive a confirmation email.",
    "contact.error_fields": "Please fill in all fields.",

    "seller.kicker": "For sellers",
    "seller.title": "Has your property been on the market too long?",
    "seller.notice": "Your property is NEVER auto-published. Woon Verder reviews your application, photos and information. Only after approval will your property go live.",
    "seller.how_title": "How it works",
    "seller.step1": "List your property (free).",
    "seller.step2": "Upload your own photos — or request Woon Verder photography.",
    "seller.step3": "We review your application and get in touch.",
    "seller.step4": "If needed, we schedule a verification appointment.",
    "seller.step5": "After approval we publish your property.",
    "seller.step6": "We match your property with active search profiles.",
    "seller.step7": "We guide viewings and bidding.",
    "seller.step8": "On successful sale a 2.5% fee applies (excl. VAT) on the sale price.",
    "seller.photo_title": "Woon Verder photography",
    "seller.photo_desc": "Professional photographer for your property. Prices on request — we prepare a tailored quote based on the type of home, floor area and desired scope.",
    "seller.photo_cta": "Request a quote",
    "seller.own_photo_title": "Provide your own photos",
    "seller.own_photo_desc": "Free — upload your own photos during onboarding. We check quality before publishing.",

    "toast.interest_sent": "Thanks, your interest has been forwarded.",
    "toast.viewing_sent": "Viewing request submitted.",
    "toast.offer_sent": "Your bid has been forwarded.",
    "toast.generic_error": "Something went wrong. Please try again.",

    "cookie.title": "Your privacy comes first",
    "cookie.desc_prefix": "Woon Verder uses cookies so the site works and to learn what can be better. You decide what to allow. Read our",
    "cookie.privacy_link": "privacy statement",
    "cookie.cat_necessary": "Necessary",
    "cookie.cat_necessary_desc": "Required for the site to function: login, language and session. Always active.",
    "cookie.cat_preferences": "Preferences",
    "cookie.cat_preferences_desc": "Remember your language, filters and searches for a better experience.",
    "cookie.cat_analytics": "Analytics",
    "cookie.cat_analytics_desc": "Anonymous page-visit measurement so we can improve the platform.",
    "cookie.cat_marketing": "Marketing",
    "cookie.cat_marketing_desc": "Not active right now. We'll ask again when this changes.",
    "cookie.always": "Always",
    "cookie.customize": "Customize",
    "cookie.save_choice": "Save my choice",
    "cookie.only_necessary": "Only necessary",
    "cookie.accept_all": "Accept all",
  },

  de: {
    "back_home": "Zurück zur Startseite",
    "read_more": "Mehr erfahren",
    "loading": "Wird geladen…",
    "yes": "Ja",
    "no": "Nein",
    "cancel": "Abbrechen",
    "save": "Speichern",
    "delete": "Löschen",
    "confirm": "Bestätigen",
    "close": "Schließen",

    "nav.properties": "Immobilien",
    "nav.how": "So funktioniert es",
    "nav.sellers": "Für Verkäufer",
    "nav.careers": "Karriere",
    "nav.about": "Über uns",
    "nav.contact": "Kontakt",
    "nav.login": "Anmelden",
    "nav.logout": "Abmelden",
    "nav.my_account": "Mein Konto",
    "nav.admin_dashboard": "Admin-Dashboard",
    "cta.submit_property": "Immobilie einreichen",
    "cta.search_property": "Immobilie finden",
    "cta.free_profile": "Kostenloses Suchprofil erstellen",
    "cta.view_property": "Ansehen",
    "cta.interest": "Ich habe Interesse",
    "cta.register_free": "Kostenloses Konto erstellen",
    "cta.have_account": "Ich habe schon ein Konto",

    "hero.badge": "Start in den Niederlanden · Internationale Ambition",
    "hero.title": "Suchen Sie nicht nur ein Haus. Finden Sie, wo Sie wirklich weiterleben können.",
    "hero.subtitle": "Woon Verder verbindet geprüfte Verkäufer direkt mit aktiven Käufern. Start in den Niederlanden. Gebaut für internationalen Maßstab.",
    "hero.tag_seller": "Vielleicht braucht Ihre Immobilie keinen neuen Preis, sondern einen neuen Käufer.",
    "hero.tag_buyer": "Sagen Sie uns, was Sie suchen. Wir suchen weiter.",
    "hero.verified": "Jede Immobilie admin-geprüft",
    "hero.never_auto": "Nie automatisch veröffentlicht",
    "hero.match_label": "Match-Score",
    "hero.match_example": "Freistehend · 4 SZ · Flevoland · <€ 550k",
    "hero.for_sellers": "Für Verkäufer",
    "hero.for_buyers": "Für Käufer",

    "section.featured": "Ausgewählte Immobilien",
    "section.how_it_works": "So funktioniert Woon Verder",
    "section.offer": "Angebot",
    "section.process": "Prozess",
    "section.all_properties": "Alle Immobilien ansehen",
    "home.step_label": "Schritt",
    "home.step1_title": "Verkäufer meldet an",
    "home.step1_desc": "Sie geben Immobiliendaten ein und laden Fotos selbst hoch — oder buchen professionelle Woon-Verder-Fotografie.",
    "home.step2_title": "Woon Verder prüft",
    "home.step2_desc": "Unser Team prüft die Anfrage, plant bei Bedarf einen Termin und kontrolliert Fotos und Daten.",
    "home.step3_title": "Matching & Veröffentlichung",
    "home.step3_desc": "Erst nach Freigabe geht die Immobilie live und wird automatisch mit passenden Suchprofilen abgeglichen.",
    "home.full_explanation": "Vollständige Erklärung",
    "home.faq_kicker": "Häufig gestellte Fragen",
    "home.faq_title": "Antworten für Käufer & Verkäufer",
    "home.seller_kicker": "Verkäufer",
    "home.seller_band_title": "Professionelle Fotografie über Woon Verder",
    "home.seller_band_desc": "Fotos, die verkaufen. Unsere Fotografen fangen Ihre Immobilie in Licht und Komposition ein, die Käufer wirklich anspricht. Fotografie ist ein separater bezahlter Service — Preise auf Anfrage. Sie können auch eigene Fotos liefern.",
    "home.seller_band_cta": "Einreichen inkl. Fotografie",
    "home.seller_band_photo_cta": "Fotografie auf Anfrage",

    "authgate.badge": "Anmeldung erforderlich",
    "authgate.title": "Melden Sie sich an, um das gesamte Angebot zu sehen",
    "authgate.subtitle": "Woon Verder ist eine geschlossene Matching-Plattform. Erstellen Sie in 30 Sekunden ein kostenloses Konto und entdecken Sie Immobilien, die wirklich zu Ihnen passen.",
    "authgate.bullet1": "Persönlicher Immobilien-Match auf Basis Ihrer Wünsche",
    "authgate.bullet2": "Sofortige Match-Score pro Immobilie",
    "authgate.bullet3": "Favoriten speichern und Gebote live verfolgen",
    "authgate.bullet4": "Kostenlos — kein Abo, keine Kreditkarte",
    "authgate.terms_prefix": "Mit der Erstellung eines Kontos akzeptieren Sie unsere",
    "authgate.terms": "AGB",
    "authgate.and": "und",
    "authgate.privacy": "Datenschutzerklärung",
    "authgate.match_label": "Ihr Match",
    "authgate.match_desc": "Basierend auf Ihrem Profil",
    "authgate.default_properties_title": "Melden Sie sich an, um das gesamte Immobilienangebot zu sehen",
    "authgate.default_property_title": "Melden Sie sich an, um diese Immobilie zu sehen",
    "authgate.default_property_subtitle": "Immobiliendetails sind für registrierte Nutzer sichtbar. Erstellen Sie in 30 Sekunden ein kostenloses Konto oder melden Sie sich an.",

    "login.title": "Anmelden",
    "login.subtitle": "Melden Sie sich in Ihrem Woon-Verder-Konto an.",
    "login.email": "E-Mail",
    "login.password": "Passwort",
    "login.submit": "Anmelden",
    "login.submitting": "Anmelden…",
    "login.no_account": "Noch kein Konto?",
    "login.register_link": "Registrieren",
    "login.welcome_back": "Willkommen zurück",

    "register.title": "Kostenloses Konto erstellen",
    "register.subtitle": "30 Sekunden — keine Kreditkarte erforderlich.",
    "register.subtitle_approval": "Neue Konten werden manuell von Woon Verder freigegeben (meist innerhalb eines Werktages). Ihre Daten landen direkt in unserem Admin-Dashboard — sobald freigegeben, können Sie sich anmelden.",
    "register.name": "Name",
    "register.email": "E-Mail",
    "register.phone": "Telefon",
    "register.phone_hint": "Optional. Internationales Format, z. B. +31 6 12 34 56 78",
    "register.country": "Land",
    "register.password": "Passwort (min. 6 Zeichen)",
    "register.role_label": "Ich bin",
    "register.role_buyer": "Käufer / Suchender",
    "register.role_seller": "Verkäufer",
    "register.submit": "Konto erstellen",
    "register.submitting": "Wird erstellt…",
    "register.have_account": "Schon ein Konto?",
    "register.login_link": "Anmelden",
    "register.created": "Konto erstellt",
    "register.err_name": "Geben Sie Ihren vollständigen Namen ein (min. 2 Zeichen)",
    "register.err_email": "Geben Sie eine gültige E-Mail-Adresse ein",
    "register.err_phone": "Ungültige Telefonnummer — 6-20 Ziffern (Leerzeichen, +, -, (), / erlaubt)",
    "register.err_country": "Geben Sie Ihr Land ein",
    "register.err_password": "Passwort muss mindestens 6 Zeichen lang sein",
    "register.err_server_unexpected": "Etwas ist schiefgelaufen. Bitte erneut versuchen oder info@woonverder.eu kontaktieren.",
    "register.country_hint": "Z. B. Deutschland, Niederlande, Spanien oder ISO-Code (DE, NL, ES…)",
    "register.pending_title": "Danke — Ihre Anfrage wurde gesendet",
    "register.pending_body_prefix": "Ihre Daten sind gespeichert unter",
    "register.pending_body_suffix": "Unser Admin sieht Ihre Anfrage sofort im Dashboard und gibt das Konto meist innerhalb eines Werktages frei. Danach können Sie sich anmelden.",
    "register.pending_step1": "Admin sieht Ihre Anfrage sofort im Woon-Verder-Dashboard",
    "register.pending_step2": "Admin gibt Ihr Konto meist innerhalb eines Werktages frei",
    "register.pending_step3": "Nach der Freigabe können Sie sich anmelden",

    "prop.verified": "✓ Geprüft",
    "prop.match_suffix": "% Match",
    "prop.slk": "SZ",
    "prop.rooms": "Zimmer",
    "prop.m2": "m²",
    "prop.energy_label": "Energielabel",

    "properties.kicker": "Angebot",
    "properties.title": "Immobilien",
    "properties.filter_city_ph": "Ort (z. B. Amsterdam)",
    "properties.filter_all_types": "Alle Immobilientypen",
    "properties.filter_min_bedrooms_ph": "Min. Schlafzimmer",
    "properties.filter_max_price_ph": "Max. Preis €",
    "properties.search": "Suchen",
    "properties.empty_title": "Keine passenden Immobilien gefunden.",
    "properties.empty_cta": "Suchprofil erstellen",

    "footer.tagline": "Suche weiter nach dem, was zu dir passt.",
    "footer.subtag": "Internationale Real-Estate-Matching-Plattform. Start in den Niederlanden, globale Ambition.",
    "footer.section_platform": "Plattform",
    "footer.section_company": "Unternehmen",
    "footer.link_search_profile": "Kostenloses Suchprofil",
    "footer.link_zoekservice": "Suchservice",
    "footer.link_mine": "Mein Woon Verder",
    "footer.link_privacy": "Datenschutz",
    "footer.link_terms": "AGB",
    "footer.link_complaints": "Beschwerden",
    "footer.rights": "Alle Rechte vorbehalten.",
    "footer.launch_line": "Start in den Niederlanden. Bereit für internationale Skalierung.",

    "contact.kicker": "Kontakt",
    "contact.title": "Wir helfen Ihnen gerne weiter.",
    "contact.subtitle": "Fragen zu Ihrer Immobilie, Ihrem Suchprofil oder unserer Fotografie? Melden Sie sich — wir antworten innerhalb eines Werktags.",
    "contact.email_label": "E-Mail",
    "contact.region_label": "Region",
    "contact.region_value": "Ganz Niederlande",
    "contact.languages_label": "Sprachen",
    "contact.form_title": "Senden Sie uns eine Nachricht",
    "contact.name": "Name",
    "contact.email_field": "E-Mail",
    "contact.subject": "Betreff",
    "contact.subj_general": "Allgemeine Frage",
    "contact.subj_seller": "Als Verkäufer",
    "contact.subj_buyer": "Als Käufer / Suchender",
    "contact.subj_photo": "Fotografie-Service",
    "contact.subj_privacy": "Datenschutz / DSGVO",
    "contact.subj_complaint": "Beschwerde",
    "contact.message": "Ihre Nachricht",
    "contact.submit": "Nachricht senden",
    "contact.sending": "Senden…",
    "contact.privacy_note_prefix": "Mit dem Absenden akzeptieren Sie unsere",
    "contact.sent_title": "Nachricht gesendet",
    "contact.sent_body": "Danke! Wir haben Ihre Nachricht erhalten und antworten innerhalb eines Werktags. Sie erhalten eine Bestätigung per E-Mail.",
    "contact.error_fields": "Bitte füllen Sie alle Felder aus.",

    "seller.kicker": "Für Verkäufer",
    "seller.title": "Steht Ihre Immobilie zu lange zum Verkauf?",
    "seller.notice": "Ihre Immobilie wird NIE automatisch veröffentlicht. Woon Verder prüft zuerst Ihre Anfrage, Fotos und Daten. Erst nach Freigabe wird die Immobilie sichtbar.",
    "seller.how_title": "So funktioniert es",
    "seller.step1": "Reichen Sie Ihre Immobilie ein (kostenlos).",
    "seller.step2": "Laden Sie eigene Fotos hoch — oder buchen Sie Woon-Verder-Fotografie.",
    "seller.step3": "Wir prüfen Ihre Anfrage und nehmen Kontakt auf.",
    "seller.step4": "Bei Bedarf vereinbaren wir einen Verifikationstermin.",
    "seller.step5": "Nach Freigabe veröffentlichen wir Ihre Immobilie.",
    "seller.step6": "Wir matchen Ihre Immobilie mit aktiven Suchprofilen.",
    "seller.step7": "Wir begleiten Besichtigungen und Gebote.",
    "seller.step8": "Bei erfolgreichem Verkauf gelten 2,5 % (exkl. MwSt.) vom Verkaufspreis.",
    "seller.photo_title": "Woon Verder Fotografie",
    "seller.photo_desc": "Professioneller Fotograf für Ihre Immobilie. Preise auf Anfrage — wir erstellen ein individuelles Angebot je nach Immobilientyp, Wohnfläche und gewünschtem Umfang.",
    "seller.photo_cta": "Angebot anfragen",
    "seller.own_photo_title": "Eigene Fotos liefern",
    "seller.own_photo_desc": "Kostenlos — laden Sie während der Anmeldung Ihre eigenen Fotos hoch. Wir prüfen die Qualität vor Veröffentlichung.",

    "toast.interest_sent": "Danke, Ihr Interesse wurde weitergeleitet.",
    "toast.viewing_sent": "Besichtigungsanfrage gesendet.",
    "toast.offer_sent": "Ihr Gebot wurde weitergeleitet.",
    "toast.generic_error": "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",

    "cookie.title": "Ihre Privatsphäre steht an erster Stelle",
    "cookie.desc_prefix": "Woon Verder verwendet Cookies, damit die Seite funktioniert und wir Verbesserungen erkennen. Sie entscheiden, was erlaubt ist. Lesen Sie unsere",
    "cookie.privacy_link": "Datenschutzerklärung",
    "cookie.cat_necessary": "Notwendig",
    "cookie.cat_necessary_desc": "Für die Funktion der Seite: Login, Sprache und Sitzung. Immer aktiv.",
    "cookie.cat_preferences": "Präferenzen",
    "cookie.cat_preferences_desc": "Sprache, Filter und Suchen werden für ein besseres Erlebnis gespeichert.",
    "cookie.cat_analytics": "Analytik",
    "cookie.cat_analytics_desc": "Anonyme Seitenaufruf-Messung, damit wir die Plattform verbessern.",
    "cookie.cat_marketing": "Marketing",
    "cookie.cat_marketing_desc": "Derzeit nicht aktiv. Bei Änderungen fragen wir erneut.",
    "cookie.always": "Immer",
    "cookie.customize": "Anpassen",
    "cookie.save_choice": "Meine Auswahl speichern",
    "cookie.only_necessary": "Nur notwendig",
    "cookie.accept_all": "Alle akzeptieren",
  },

  es: {
    "back_home": "Volver al inicio",
    "read_more": "Más información",
    "loading": "Cargando…",
    "yes": "Sí",
    "no": "No",
    "cancel": "Cancelar",
    "save": "Guardar",
    "delete": "Eliminar",
    "confirm": "Confirmar",
    "close": "Cerrar",

    "nav.properties": "Propiedades",
    "nav.how": "Cómo funciona",
    "nav.sellers": "Para Vendedores",
    "nav.careers": "Empleo",
    "nav.about": "Sobre nosotros",
    "nav.contact": "Contacto",
    "nav.login": "Iniciar sesión",
    "nav.logout": "Cerrar sesión",
    "nav.my_account": "Mi cuenta",
    "nav.admin_dashboard": "Panel admin",
    "cta.submit_property": "Publicar mi propiedad",
    "cta.search_property": "Buscar propiedad",
    "cta.free_profile": "Crea un perfil gratuito",
    "cta.view_property": "Ver propiedad",
    "cta.interest": "Me interesa",
    "cta.register_free": "Crear cuenta gratis",
    "cta.have_account": "Ya tengo una cuenta",

    "hero.badge": "Lanzamiento en Países Bajos · Ambición internacional",
    "hero.title": "No busques solo una casa. Encuentra dónde realmente puedes vivir más allá.",
    "hero.subtitle": "Woon Verder conecta vendedores verificados directamente con compradores activos. Lanzamiento en Países Bajos. Construido para escala internacional.",
    "hero.tag_seller": "Quizás su casa no necesita un nuevo precio, sino un nuevo comprador.",
    "hero.tag_buyer": "Díganos qué busca. Buscamos más allá.",
    "hero.verified": "Cada propiedad verificada por admin",
    "hero.never_auto": "Nunca publicada automáticamente",
    "hero.match_label": "Puntuación de match",
    "hero.match_example": "Chalet · 4 hab · Flevoland · <€ 550k",
    "hero.for_sellers": "Para vendedores",
    "hero.for_buyers": "Para compradores",

    "section.featured": "Propiedades destacadas",
    "section.how_it_works": "Cómo funciona Woon Verder",
    "section.offer": "Oferta",
    "section.process": "Proceso",
    "section.all_properties": "Ver todas las propiedades",
    "home.step_label": "Paso",
    "home.step1_title": "El vendedor se registra",
    "home.step1_desc": "Rellena los datos y sube fotos, o pide fotografía profesional de Woon Verder.",
    "home.step2_title": "Woon Verder revisa",
    "home.step2_desc": "Nuestro equipo revisa la solicitud, agenda una cita si es necesario y verifica fotos y datos.",
    "home.step3_title": "Match y publicación",
    "home.step3_desc": "Solo tras la aprobación se publica la propiedad y se hace match con perfiles de búsqueda.",
    "home.full_explanation": "Explicación completa",
    "home.faq_kicker": "Preguntas frecuentes",
    "home.faq_title": "Respuestas para compradores y vendedores",
    "home.seller_kicker": "Vendedores",
    "home.seller_band_title": "Fotografía profesional por Woon Verder",
    "home.seller_band_desc": "Fotos que venden. Nuestros fotógrafos capturan su vivienda con la luz y composición que atrae compradores. Fotografía es un servicio de pago aparte — precios a consultar. También puede aportar sus propias fotos.",
    "home.seller_band_cta": "Publicar con fotografía",
    "home.seller_band_photo_cta": "Fotografía a consultar",

    "authgate.badge": "Requiere inicio de sesión",
    "authgate.title": "Inicie sesión para ver todo el catálogo",
    "authgate.subtitle": "Woon Verder es una plataforma de match cerrada. Crea una cuenta gratis en 30 segundos y descubre viviendas que realmente encajan contigo.",
    "authgate.bullet1": "Match personal según tus deseos",
    "authgate.bullet2": "Puntuación de match instantánea por propiedad",
    "authgate.bullet3": "Guarda favoritos y sigue las ofertas en vivo",
    "authgate.bullet4": "Gratis — sin suscripción, sin tarjeta",
    "authgate.terms_prefix": "Al crear una cuenta aceptas nuestras",
    "authgate.terms": "condiciones",
    "authgate.and": "y",
    "authgate.privacy": "política de privacidad",
    "authgate.match_label": "Tu match",
    "authgate.match_desc": "Basado en tu perfil",
    "authgate.default_properties_title": "Inicie sesión para ver todo el catálogo de propiedades",
    "authgate.default_property_title": "Inicie sesión para ver esta propiedad",
    "authgate.default_property_subtitle": "Los detalles son visibles para usuarios registrados. Crea una cuenta gratis en 30 segundos o inicia sesión.",

    "login.title": "Iniciar sesión",
    "login.subtitle": "Accede a tu cuenta de Woon Verder.",
    "login.email": "Email",
    "login.password": "Contraseña",
    "login.submit": "Iniciar sesión",
    "login.submitting": "Iniciando…",
    "login.no_account": "¿Aún no tienes cuenta?",
    "login.register_link": "Registrarse",
    "login.welcome_back": "Bienvenido de vuelta",

    "register.title": "Crear cuenta gratis",
    "register.subtitle": "30 segundos — sin tarjeta.",
    "register.subtitle_approval": "Las cuentas nuevas son aprobadas manualmente por Woon Verder (normalmente en 1 día laborable). Tus datos llegan directamente a nuestro panel de admin — una vez aprobada podrás iniciar sesión.",
    "register.name": "Nombre",
    "register.email": "Email",
    "register.phone": "Teléfono",
    "register.phone_hint": "Opcional. Formato internacional, p. ej. +31 6 12 34 56 78",
    "register.country": "País",
    "register.password": "Contraseña (mín. 6 caracteres)",
    "register.role_label": "Soy",
    "register.role_buyer": "Comprador / buscador",
    "register.role_seller": "Vendedor",
    "register.submit": "Crear cuenta",
    "register.submitting": "Creando…",
    "register.have_account": "¿Ya tienes cuenta?",
    "register.login_link": "Iniciar sesión",
    "register.created": "Cuenta creada",
    "register.err_name": "Introduce tu nombre completo (mín. 2 caracteres)",
    "register.err_email": "Introduce un email válido",
    "register.err_phone": "Teléfono no válido — 6-20 dígitos (espacios, +, -, (), / permitidos)",
    "register.err_country": "Introduce tu país",
    "register.err_password": "La contraseña debe tener al menos 6 caracteres",
    "register.err_server_unexpected": "Algo salió mal. Inténtalo de nuevo o contacta info@woonverder.eu.",
    "register.country_hint": "P. ej. España, Países Bajos, Alemania o código ISO (ES, NL, DE…)",
    "register.pending_title": "Gracias — tu solicitud se ha enviado",
    "register.pending_body_prefix": "Tus datos están guardados bajo",
    "register.pending_body_suffix": "Nuestro admin ve tu solicitud al instante en el panel y suele aprobar en 1 día laborable. Después podrás iniciar sesión.",
    "register.pending_step1": "El admin ve tu solicitud al instante en el panel de Woon Verder",
    "register.pending_step2": "El admin suele aprobar tu cuenta en 1 día laborable",
    "register.pending_step3": "Una vez aprobada podrás iniciar sesión",

    "prop.verified": "✓ Verificada",
    "prop.match_suffix": "% Match",
    "prop.slk": "hab",
    "prop.rooms": "habitaciones",
    "prop.m2": "m²",
    "prop.energy_label": "Etiqueta energética",

    "properties.kicker": "Oferta",
    "properties.title": "Propiedades",
    "properties.filter_city_ph": "Ciudad (ej. Amsterdam)",
    "properties.filter_all_types": "Todos los tipos",
    "properties.filter_min_bedrooms_ph": "Mín. dormitorios",
    "properties.filter_max_price_ph": "Precio máx €",
    "properties.search": "Buscar",
    "properties.empty_title": "No se encontraron propiedades.",
    "properties.empty_cta": "Crear un perfil de búsqueda",

    "footer.tagline": "Busca más allá lo que te encaja.",
    "footer.subtag": "Plataforma internacional de match inmobiliario. Lanzamiento en Países Bajos, ambición global.",
    "footer.section_platform": "Plataforma",
    "footer.section_company": "Empresa",
    "footer.link_search_profile": "Perfil de búsqueda gratis",
    "footer.link_zoekservice": "Servicio de búsqueda",
    "footer.link_mine": "Mi Woon Verder",
    "footer.link_privacy": "Privacidad",
    "footer.link_terms": "Términos",
    "footer.link_complaints": "Reclamaciones",
    "footer.rights": "Todos los derechos reservados.",
    "footer.launch_line": "Lanzamiento en Países Bajos. Listo para escala internacional.",

    "contact.kicker": "Contacto",
    "contact.title": "Estamos aquí para ayudarte.",
    "contact.subtitle": "¿Preguntas sobre tu propiedad, perfil de búsqueda o fotografía? Contáctanos — respondemos en un día laborable.",
    "contact.email_label": "Email",
    "contact.region_label": "Región",
    "contact.region_value": "Toda Países Bajos",
    "contact.languages_label": "Idiomas",
    "contact.form_title": "Envíanos un mensaje",
    "contact.name": "Nombre",
    "contact.email_field": "Email",
    "contact.subject": "Asunto",
    "contact.subj_general": "Consulta general",
    "contact.subj_seller": "Como vendedor",
    "contact.subj_buyer": "Como comprador / buscador",
    "contact.subj_photo": "Servicio de fotografía",
    "contact.subj_privacy": "Privacidad / RGPD",
    "contact.subj_complaint": "Reclamación",
    "contact.message": "Tu mensaje",
    "contact.submit": "Enviar mensaje",
    "contact.sending": "Enviando…",
    "contact.privacy_note_prefix": "Al enviar aceptas nuestra",
    "contact.sent_title": "Mensaje enviado",
    "contact.sent_body": "¡Gracias! Hemos recibido tu mensaje y responderemos en un día laborable. También recibirás una confirmación por email.",
    "contact.error_fields": "Por favor rellena todos los campos.",

    "seller.kicker": "Para vendedores",
    "seller.title": "¿Su propiedad lleva mucho tiempo en venta?",
    "seller.notice": "Su propiedad NUNCA se publica automáticamente. Woon Verder revisa su solicitud, fotos y datos. Solo tras la aprobación se hace visible.",
    "seller.how_title": "Cómo funciona",
    "seller.step1": "Publique su propiedad (gratis).",
    "seller.step2": "Suba sus propias fotos — o solicite fotografía Woon Verder.",
    "seller.step3": "Revisamos su solicitud y le contactamos.",
    "seller.step4": "Si es necesario agendamos una cita de verificación.",
    "seller.step5": "Tras la aprobación publicamos su propiedad.",
    "seller.step6": "Hacemos match con perfiles de búsqueda activos.",
    "seller.step7": "Acompañamos visitas y pujas.",
    "seller.step8": "Al venderse aplicamos un 2,5 % (excl. IVA) sobre el precio de venta.",
    "seller.photo_title": "Fotografía Woon Verder",
    "seller.photo_desc": "Fotógrafo profesional para su propiedad. Precios a consultar — preparamos un presupuesto a medida según el tipo de vivienda, la superficie y el alcance deseado.",
    "seller.photo_cta": "Solicitar presupuesto",
    "seller.own_photo_title": "Aporte sus propias fotos",
    "seller.own_photo_desc": "Gratis — sube tus fotos durante el alta. Verificamos la calidad antes de publicar.",

    "toast.interest_sent": "Gracias, tu interés se ha registrado.",
    "toast.viewing_sent": "Solicitud de visita enviada.",
    "toast.offer_sent": "Tu oferta ha sido enviada.",
    "toast.generic_error": "Algo salió mal. Inténtalo de nuevo.",

    "cookie.title": "Tu privacidad es lo primero",
    "cookie.desc_prefix": "Woon Verder usa cookies para que el sitio funcione y para saber qué mejorar. Tú decides qué permitir. Lee nuestra",
    "cookie.privacy_link": "política de privacidad",
    "cookie.cat_necessary": "Necesarias",
    "cookie.cat_necessary_desc": "Para que el sitio funcione: login, idioma y sesión. Siempre activas.",
    "cookie.cat_preferences": "Preferencias",
    "cookie.cat_preferences_desc": "Recordamos idioma, filtros y búsquedas para una mejor experiencia.",
    "cookie.cat_analytics": "Analíticas",
    "cookie.cat_analytics_desc": "Medición anónima de visitas para mejorar la plataforma.",
    "cookie.cat_marketing": "Marketing",
    "cookie.cat_marketing_desc": "No activo. Cuando cambie te lo volveremos a preguntar.",
    "cookie.always": "Siempre",
    "cookie.customize": "Personalizar",
    "cookie.save_choice": "Guardar mi elección",
    "cookie.only_necessary": "Solo necesarias",
    "cookie.accept_all": "Aceptar todo",
  },
};

const I18nCtx = createContext({ lang: "nl", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("wv_lang") || "nl");
  useEffect(() => { localStorage.setItem("wv_lang", lang); document.documentElement.lang = lang; }, [lang]);
  const t = (k) => (dict[lang] && dict[lang][k]) || dict.nl[k] || k;
  return <I18nCtx.Provider value={{ lang, setLang: setLangState, t }}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);

/**
 * tx — inline translation helper for ad-hoc strings that don't need a dictionary key.
 * Usage: const { lang } = useI18n(); tx(lang, { nl: "...", en: "...", de: "...", es: "..." })
 * Falls back to NL when the language is missing.
 */
export const tx = (lang, obj) => (obj && (obj[lang] || obj.nl)) || "";

export const LANGS = [
  { code: "nl", label: "NL", name: "Nederlands" },
  { code: "en", label: "EN", name: "English" },
  { code: "de", label: "DE", name: "Deutsch" },
  { code: "es", label: "ES", name: "Español" },
];
